import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const semaglutide: ContentBlock<"medication"> = {
  id: makeId("med-semaglutide"),
  kind: "medication",
  tags: ["medications", "her", "pre-cycle", "metabolic", "pcos"],
  title: "Ozempic / Wegovy",
  subtitle:
    "Semaglutide - GLP-1 medication used for metabolic support before pregnancy",
  body: {
    genericName: "Semaglutide",
    drugClass: "GLP-1 receptor agonist",
    purpose:
      "A GLP-1 receptor agonist used for type 2 diabetes or weight management before fertility treatment. In fertility care, it may appear in preconception plans for patients with obesity, insulin resistance, or PMOS-related metabolic risk (ex PCOS). It is not a fertility medication and should not be used during pregnancy attempts unless the prescribing clinician and fertility clinic have aligned on timing.",
    commonRoutes: ["subcutaneous", "oral"],
    commonForms: ["pen", "pill"],
    dosageRanges:
      "Dose depends on product and indication. Injectable semaglutide is usually titrated weekly; oral semaglutide is taken daily under the prescribing clinician's plan.",
    howToTake:
      "Step 1: Confirm timing - Tell your fertility clinic if you use semaglutide. Because of the long washout period, many labels advise stopping semaglutide at least 2 months before a planned pregnancy. | Step 2: Take exactly as prescribed - Injectable products are usually taken once weekly on the same day. Oral semaglutide has specific empty-stomach instructions; follow the product label. | Step 3: Watch GI symptoms - Nausea, vomiting, constipation, or poor intake can affect hydration, nutrition, and procedure planning. | ⚠ When to call - Call urgently for severe abdominal pain, persistent vomiting, dehydration, symptoms of gallbladder disease, or signs of pancreatitis.",
    sideEffects: [
      "Nausea",
      "Vomiting or diarrhea",
      "Constipation",
      "Abdominal pain",
      "Reduced appetite",
      "Gallbladder symptoms",
      "Rare pancreatitis",
      "Possible dehydration if vomiting is persistent",
    ],
    storage:
      "Follow the product label. Injectable pens often require refrigeration before first use and have product-specific room-temperature limits after first use.",
    aliases: ["Semaglutide", "Ozempic", "Wegovy", "Rybelsus"],
    forWhom: "her",
    availability: [
      {
        name: "Ozempic",
        manufacturer: "Novo Nordisk",
        countries: ["US", "GB", "EU", "CA", "AU"],
      },
      {
        name: "Wegovy",
        manufacturer: "Novo Nordisk",
        countries: ["US", "GB", "EU", "CA", "AU"],
      },
      {
        name: "Rybelsus",
        manufacturer: "Novo Nordisk",
        countries: ["US", "GB", "EU", "CA", "AU"],
      },
    ],
    resources: [
      {
        label: "Wegovy prescribing information",
        url: "https://www.novo-pi.com/wegovy.pdf",
        type: "info",
        market: "US",
        authority: "manufacturer",
        audience: "regulatory",
        medicationMatch: "Wegovy (semaglutide)",
        verifiedOn: "2026-07-17",
      },
    ],
    frBody: {
      genericName: "Sémaglutide",
      drugClass: "Agoniste du récepteur GLP-1",
      purpose:
        "Agoniste du récepteur GLP-1 utilisé dans le diabète de type 2 ou la prise en charge du poids avant un traitement de fertilité. En parcours de fertilité, il peut figurer dans les plans préconceptionnels chez les patientes avec obésité, insulinorésistance ou risque métabolique de type SOPK. Ce n'est pas un médicament de fertilité et il ne doit pas être utilisé pendant les tentatives de grossesse, sauf si le médecin prescripteur et votre centre de fertilité se sont accordés sur le calendrier.",
      dosageRanges:
        "La dose dépend du produit et de l'indication. Le sémaglutide injectable est généralement titré chaque semaine ; le sémaglutide oral se prend quotidiennement selon le schéma défini par le médecin prescripteur.",
      howToTake:
        "Confirmer le calendrier - Informez votre centre de fertilité si vous utilisez le sémaglutide. En raison du long délai d'élimination, de nombreuses notices recommandent d'arrêter le sémaglutide au moins 2 mois avant une grossesse planifiée. | Prendre exactement comme prescrit - Les formes injectables se prennent généralement une fois par semaine, le même jour. Le sémaglutide oral nécessite une prise à jeun selon des consignes précises ; suivez la notice du produit. | Surveiller les symptômes digestifs - Les nausées, vomissements, constipation ou prises alimentaires insuffisantes peuvent affecter l'hydratation, la nutrition et la planification des procédures. | ⚠ Quand appeler - Contactez en urgence en cas de douleurs abdominales sévères, vomissements persistants, déshydratation, symptômes de maladie de la vésicule biliaire ou signes de pancréatite.",
      sideEffects: [
        "Nausées",
        "Vomissements ou diarrhée",
        "Constipation",
        "Douleurs abdominales",
        "Diminution de l'appétit",
        "Symptômes de la vésicule biliaire",
        "Pancréatite (rare)",
        "Déshydratation possible en cas de vomissements persistants",
      ],
      storage:
        "Suivez la notice du produit. Les stylos injectables doivent généralement être conservés au réfrigérateur avant la première utilisation et ont des limites de conservation à température ambiante spécifiques au produit après ouverture.",
    },
  },
  sources: [
    {
      title: "Wegovy prescribing information",
      journal: "Novo Nordisk / FDA labeling",
      year: 2026,
      url: "https://www.novo-pi.com/wegovy.pdf",
      evidenceLevel: "expert-opinion",
    },
  ],
};

const tirzepatide: ContentBlock<"medication"> = {
  id: makeId("med-tirzepatide"),
  kind: "medication",
  tags: ["medications", "her", "pre-cycle", "metabolic", "pcos"],
  title: "Mounjaro / Zepbound",
  subtitle:
    "Tirzepatide - GIP/GLP-1 medication used for metabolic support before pregnancy",
  body: {
    genericName: "Tirzepatide",
    drugClass: "Dual GIP/GLP-1 receptor agonist",
    purpose:
      "A dual GIP/GLP-1 receptor agonist used for type 2 diabetes or weight management. It may be used before fertility treatment as part of a metabolic optimization plan, but it is not a fertility medication and needs explicit preconception stop timing.",
    commonRoutes: ["subcutaneous"],
    commonForms: ["pen"],
    dosageRanges:
      "Usually titrated once weekly. Product, dose, and escalation timing are set by the prescribing clinician.",
    howToTake:
      "Step 1: Confirm timing - Tell your fertility clinic and prescriber before trying to conceive or starting treatment. | Step 2: Inject weekly - Use the pen exactly as prescribed, rotating injection sites and following the storage instructions. | Step 3: Contraception timing - Tirzepatide can reduce oral contraceptive effectiveness around initiation and dose escalation because it delays gastric emptying; labels advise switching to non-oral contraception or adding barrier contraception for the specified window. | ⚠ When to call - Call urgently for severe abdominal pain, persistent vomiting, dehydration, gallbladder symptoms, or signs of pancreatitis.",
    sideEffects: [
      "Nausea",
      "Vomiting or diarrhea",
      "Constipation",
      "Abdominal pain",
      "Reduced appetite",
      "Injection-site reactions",
      "Gallbladder symptoms",
      "Rare pancreatitis",
    ],
    storage:
      "Follow the product label. Pens are typically refrigerated before use and may have product-specific room-temperature limits.",
    aliases: ["Tirzepatide", "Mounjaro", "Zepbound"],
    forWhom: "her",
    availability: [
      {
        name: "Mounjaro",
        manufacturer: "Eli Lilly",
        countries: ["US", "GB", "EU", "CA", "AU"],
      },
      { name: "Zepbound", manufacturer: "Eli Lilly", countries: ["US"] },
    ],
    resources: [
      {
        label: "Zepbound prescribing information",
        url: "https://pi.lilly.com/us/zepbound-uspi.pdf",
        type: "info",
        market: "US",
        authority: "manufacturer",
        audience: "regulatory",
        medicationMatch: "Zepbound (tirzepatide)",
        verifiedOn: "2026-07-17",
      },
    ],
    frBody: {
      genericName: "Tirzépatide",
      drugClass: "Double agoniste des récepteurs GIP/GLP-1",
      purpose:
        "Double agoniste des récepteurs GIP/GLP-1 utilisé dans le diabète de type 2 ou la prise en charge du poids. Il peut être utilisé avant un traitement de fertilité dans le cadre d'une optimisation métabolique, mais ce n'est pas un médicament de fertilité et nécessite un calendrier d'arrêt préconceptionnel explicite.",
      dosageRanges:
        "Habituellement titré une fois par semaine. Le produit, la dose et le rythme d'escalade sont définis par le médecin prescripteur.",
      howToTake:
        "Confirmer le calendrier - Informez votre centre de fertilité et votre prescripteur avant d'essayer de concevoir ou de commencer un traitement. | Injecter une fois par semaine - Utilisez le stylo exactement comme prescrit, en alternant les sites d'injection et en respectant les consignes de conservation. | Calendrier de la contraception - Le tirzépatide peut réduire l'efficacité des contraceptifs oraux lors de l'initiation et de l'escalade de dose, car il ralentit la vidange gastrique ; les notices recommandent de passer à une contraception non orale ou d'ajouter une contraception barrière pendant la période indiquée. | ⚠ Quand appeler - Contactez en urgence en cas de douleurs abdominales sévères, vomissements persistants, déshydratation, symptômes de la vésicule biliaire ou signes de pancréatite.",
      sideEffects: [
        "Nausées",
        "Vomissements ou diarrhée",
        "Constipation",
        "Douleurs abdominales",
        "Diminution de l'appétit",
        "Réactions au point d'injection",
        "Symptômes de la vésicule biliaire",
        "Pancréatite (rare)",
      ],
      storage:
        "Suivez la notice du produit. Les stylos doivent généralement être conservés au réfrigérateur avant utilisation et peuvent avoir des limites de conservation à température ambiante spécifiques au produit.",
    },
  },
  sources: [
    {
      title: "Zepbound prescribing information",
      journal: "Eli Lilly / FDA labeling",
      year: 2026,
      url: "https://pi.lilly.com/us/zepbound-uspi.pdf",
      evidenceLevel: "expert-opinion",
    },
  ],
};

const liraglutide: ContentBlock<"medication"> = {
  id: makeId("med-liraglutide"),
  kind: "medication",
  tags: ["medications", "her", "pre-cycle", "metabolic", "pcos"],
  title: "Saxenda / Victoza",
  subtitle:
    "Liraglutide - daily GLP-1 medication used for metabolic support before pregnancy",
  body: {
    genericName: "Liraglutide",
    drugClass: "GLP-1 receptor agonist",
    purpose:
      "A daily GLP-1 receptor agonist used for type 2 diabetes or weight management. In fertility care, it may appear before treatment for metabolic optimization, especially with obesity, insulin resistance, or PMOS (ex PCOS). It is not used as a fertility medication and should be reviewed before pregnancy attempts.",
    commonRoutes: ["subcutaneous"],
    commonForms: ["pen"],
    dosageRanges:
      "Daily dosing is titrated by indication and tolerability under the prescribing clinician's plan.",
    howToTake:
      "Step 1: Confirm timing - Tell your fertility clinic if you use liraglutide before trying to conceive. | Step 2: Inject daily - Use at the same time each day if instructed, rotate injection sites, and follow the pen storage instructions. | Step 3: Watch oral medication timing - Liraglutide delays gastric emptying and can affect absorption timing for some oral medications. | ⚠ When to call - Call urgently for severe abdominal pain, persistent vomiting, dehydration, gallbladder symptoms, or signs of pancreatitis.",
    sideEffects: [
      "Nausea",
      "Vomiting or diarrhea",
      "Constipation",
      "Abdominal pain",
      "Reduced appetite",
      "Injection-site reactions",
      "Gallbladder symptoms",
      "Rare pancreatitis",
    ],
    storage:
      "Follow the product label. Pens are typically refrigerated before first use and have product-specific room-temperature limits after first use.",
    aliases: ["Liraglutide", "Saxenda", "Victoza"],
    forWhom: "her",
    availability: [
      {
        name: "Saxenda",
        manufacturer: "Novo Nordisk",
        countries: ["US", "GB", "EU", "CA", "AU"],
      },
      {
        name: "Victoza",
        manufacturer: "Novo Nordisk",
        countries: ["US", "GB", "EU", "CA", "AU"],
      },
    ],
    resources: [
      {
        label: "Saxenda prescribing information",
        url: "https://www.novo-pi.com/saxenda.pdf",
        type: "info",
        market: "US",
        authority: "manufacturer",
        audience: "regulatory",
        medicationMatch: "Saxenda (liraglutide)",
        verifiedOn: "2026-07-17",
      },
    ],
    frBody: {
      genericName: "Liraglutide",
      drugClass: "Agoniste du récepteur GLP-1",
      purpose:
        "Agoniste quotidien du récepteur GLP-1 utilisé dans le diabète de type 2 ou la prise en charge du poids. En parcours de fertilité, il peut être prescrit avant le traitement pour optimiser le profil métabolique, en particulier en cas d'obésité, d'insulinorésistance ou de SOPK. Il n'est pas utilisé comme médicament de fertilité et doit être réévalué avant toute tentative de grossesse.",
      dosageRanges:
        "La dose quotidienne est titrée en fonction de l'indication et de la tolérance, selon le schéma défini par le médecin prescripteur.",
      howToTake:
        "Confirmer le calendrier - Informez votre centre de fertilité si vous utilisez le liraglutide avant d'essayer de concevoir. | Injecter chaque jour - Utilisez à la même heure chaque jour si cela vous a été indiqué, alternez les sites d'injection et suivez les consignes de conservation du stylo. | Surveiller le calendrier des médicaments oraux - Le liraglutide ralentit la vidange gastrique et peut modifier le délai d'absorption de certains médicaments oraux. | ⚠ Quand appeler - Contactez en urgence en cas de douleurs abdominales sévères, vomissements persistants, déshydratation, symptômes de la vésicule biliaire ou signes de pancréatite.",
      sideEffects: [
        "Nausées",
        "Vomissements ou diarrhée",
        "Constipation",
        "Douleurs abdominales",
        "Diminution de l'appétit",
        "Réactions au point d'injection",
        "Symptômes de la vésicule biliaire",
        "Pancréatite (rare)",
      ],
      storage:
        "Suivez la notice du produit. Les stylos doivent généralement être conservés au réfrigérateur avant la première utilisation et ont des limites de conservation à température ambiante spécifiques au produit après ouverture.",
    },
  },
  sources: [
    {
      title: "Saxenda prescribing information",
      journal: "Novo Nordisk / FDA labeling",
      year: 2026,
      url: "https://www.novo-pi.com/saxenda.pdf",
      evidenceLevel: "expert-opinion",
    },
  ],
};

registerBlock(semaglutide);
registerBlock(tirzepatide);
registerBlock(liraglutide);
